/**
 * The router seam.
 *
 * Everything in this package that navigates, links, or reads the URL goes
 * through here. Backed by React Router; the six frontends import
 * `@booking/core-ui/router` and never name a router library themselves.
 *
 * # WHY THE SEAM SURVIVES ITS OWN MIGRATION
 *
 * It was built in Phase 10 so the Next→Vite conversion could go one app at a
 * time: a twin file per router, swapped per app by a Vite plugin. That is over —
 * `router.ts`, `router.rr.tsx` and `vite-router-plugin.ts` are all gone, and this
 * is the only implementation. The seam itself stays because it is what makes the
 * next such change a one-file edit rather than 44 call sites.
 *
 * # THE SHAPES ARE THIS PACKAGE'S, NOT REACT ROUTER'S
 *
 * A deliberate Phase 10 call, kept because it is still right: an adapter that
 * copies one library's API exactly is not an adapter, it is an alias, and it
 * moves the work to the call sites.
 *
 *   - `useSearchParams` returns the params DIRECTLY. React Router returns a
 *     `[params, setParams]` tuple, and all 34 reads in this repo are `.get()`;
 *     exposing a setter nobody uses would mean 22 call sites destructuring to
 *     ignore it.
 *   - `Link` keeps `href`. React Router spells it `to`. One translation here
 *     beats renaming 13 JSX sites.
 */

import {
  Link as RouterLink,
  useLocation as useRouterLocation,
  useNavigate as useRouterNavigate,
  useParams as useRouterParams,
  useSearchParams as useRouterSearchParams,
} from "react-router-dom";
import { useCallback, useMemo, type ComponentProps } from "react";

/** Navigate to a path. `replace` swaps the history entry instead of pushing. */
export type NavigateFunction = (to: string, options?: { replace?: boolean }) => void;

export function useNavigate(): NavigateFunction {
  const navigate = useRouterNavigate();
  return useCallback(
    (to, options) => {
      navigate(to, { replace: options?.replace === true });
    },
    [navigate],
  );
}

/**
 * The current location.
 *
 * Only `pathname` and `search`, because only `pathname` is read today
 * (AdminLayout, for the active nav item and the staff route gate) and `search`
 * is the obvious neighbour. React Router also carries `state` and `key`; they
 * are not exposed until something needs them, so the seam stays narrow.
 */
export interface Location {
  pathname: string;
  search: string;
}

export function useLocation(): Location {
  const location = useRouterLocation();
  // React Router's `search` already carries the leading "?" and is "" when
  // empty, which is the contract callers rely on.
  return useMemo(
    () => ({ pathname: location.pathname, search: location.search }),
    [location.pathname, location.search],
  );
}

/**
 * The query string, read-only.
 *
 * Callers put this in dependency arrays, so the identity has to be stable across
 * renders for a given URL. React Router's tuple element is stable per URL, so
 * returning it directly preserves that without a wrapper object.
 */
export interface ReadonlySearchParams {
  get(name: string): string | null;
}

export function useSearchParams(): ReadonlySearchParams {
  const [params] = useRouterSearchParams();
  return params;
}

/** The dynamic segments of the matched route. */
export function useParams<
  T extends Record<string, string | undefined> = Record<string, string | undefined>,
>(): T {
  return useRouterParams() as T;
}

/**
 * A client-side link, spelling the target `href`.
 *
 * `href` maps to React Router's `to`. The rest of the surface used in this repo
 * is `className`, `onClick` and children, all of which pass through unchanged.
 */
export type LinkProps = Omit<ComponentProps<typeof RouterLink>, "to"> & { href: string };

export function Link({ href, ...rest }: LinkProps) {
  return <RouterLink to={href} {...rest} />;
}
